import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';

import PartnerCTASection from '../components/PartnerCTASection';
import NewsletterSignup from '../components/NewsletterSignup';
import DocumentHead from '../components/DocumentHead';
import { trackPartnerEvent } from '../hooks/useCandidaturaAnalytics';
import heroProgetto from '../assets/heroClienti.webp';
import logo from '../assets/logo.png';

const MangItaliaText = ({ children, className = '', isTitle = false }) => (
  <span
    className={`font-sacramento ${isTitle ? 'text-lg' : 'text-base'} ${className}`}
    style={{ fontSize: isTitle ? '1.2em' : '1.15em' }}
  >
    {children}
  </span>
);

const CATEGORIE_PREVIEW = [
  'Latticini',
  'Salumi',
  'Olio EVO',
  'Pasta',
  'Conserve',
  'Frutta secca',
  'Pesto & salse',
];

const sections = [
  {
    title: "Cos'è Mangitalia?",
    text: (
      <>
        <MangItaliaText>Mangitalia</MangItaliaText> collega produttori alimentari artigianali italiani
        a una community di consumatori consapevoli. Formaggi, salumi, olio, pasta, conserve e altre
        eccellenze: ogni prodotto racconta territorio, filiera e qualità selezionata.
      </>
    ),
  },
  {
    title: 'Come funziona?',
    text: (
      <>
        Esplora il catalogo per categoria e territorio, ricevi a casa prodotti curati per filiera e
        trasporto. Sei produttore? Candidati online: valutazione qualità, poi ingresso in catalogo
        con logistica gestita in piattaforma.
      </>
    ),
  },
  {
    title: 'Perché è diverso',
    text: (
      <>
        Non è un marketplace qualsiasi: le categorie sono selezionate, i produttori valutati,
        il prezzo di catalogo chiaro. Fresco e secco convivono nella stessa esperienza Italia.
      </>
    ),
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--mang-bg,#FFFEFA)] relative">
      <DocumentHead
        title="Mangitalia — Eccellenze alimentari italiane"
        description="Prodotti alimentari italiani selezionati: dal produttore artigianale a casa tua. Catalogo, candidature produttori e filiera curata."
        path="/"
      />


      {/* Hero full-bleed — -mt-14 per estendersi dietro la navbar trasparente */}
      <section
        className="relative w-full min-h-[90vh] bg-cover bg-center flex items-end md:items-center -mt-14"
        style={{ backgroundImage: `url(${heroProgetto})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/25" />
        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 pb-16 pt-24 md:pt-32 md:pb-20 text-white">
          <motion.img
            src={logo}
            alt="Mangitalia"
            className="w-40 md:w-48 mb-6 drop-shadow-lg"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          />
          <motion.h1
            className="font-sacramento text-5xl md:text-7xl mb-4 leading-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            Mangitalia
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl max-w-xl text-white/90 mb-8 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Eccellenze alimentari italiane selezionate: dal produttore a casa tua.
          </motion.p>
          <motion.div
            className="flex flex-wrap gap-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <Link
              to="/catalogo"
              onClick={() => trackPartnerEvent('home_cta', { action: 'catalogo' })}
              className="mang-gradient text-white px-8 py-3.5 rounded-full font-bold shadow-xl"
            >
              Esplora il catalogo
            </Link>
            <Link
              to="/diventa-partner"
              onClick={() => trackPartnerEvent('home_cta', { action: 'partner' })}
              className="bg-white/15 backdrop-blur border border-white/50 text-white px-8 py-3.5 rounded-full font-bold hover:bg-white/25"
            >
              Diventa partner
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Categorie — una riga, non card clutter */}
      <section className="border-b border-[#f3deb8] bg-white/80">
        <div className="max-w-5xl mx-auto px-4 py-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <span className="text-xs uppercase tracking-widest text-[#c78244] font-semibold w-full text-center sm:w-auto sm:mr-2">
            Categorie
          </span>
          {CATEGORIE_PREVIEW.map((c) => (
            <Link
              key={c}
              to="/catalogo"
              className="text-sm text-gray-600 hover:text-[#c78244] transition-colors"
            >
              {c}
            </Link>
          ))}
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-14">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#c78244] mb-4">
            La tradizione senza confini
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed">
            <MangItaliaText>Mangitalia</MangItaliaText> valorizza i prodotti alimentari locali e
            collega chi produce con chi cerca autenticità — in tutta Italia.
          </p>
        </motion.div>

        <h3 className="text-xl md:text-2xl font-bold text-[#c78244] text-center mb-6">
          Scopri di più
        </h3>

        <div className="space-y-3 mb-10">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              className="bg-white rounded-2xl shadow-sm border border-[#f3deb8]/60 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
            >
              <button
                type="button"
                className="w-full flex justify-between items-center px-4 py-3.5 text-left text-lg font-semibold text-[#c78244]"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                {section.title}
                {openIndex === index ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              <motion.div
                initial={false}
                animate={openIndex === index ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="px-4 text-gray-700 text-base overflow-hidden"
              >
                <div className="pb-4">{section.text}</div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-4">
          <Link
            to="/catalogo"
            className="text-[#c78244] font-semibold underline underline-offset-2"
          >
            Vai al catalogo →
          </Link>
          <Link
            to="/candidatura"
            className="text-gray-500 hover:text-[#c78244] underline underline-offset-2"
          >
            Candidatura produttori
          </Link>
        </div>
      </div>

      <PartnerCTASection />

      {/* Sezione Guadagna con Mangitalia */}
      <motion.section
        className="bg-[#fff8ea] border-y border-[#f3deb8] py-14 px-6"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block bg-[#F6CF48]/30 text-[#7a4a1f] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Per chi fa parte della community
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#c78244] mb-4 leading-tight">
            Porta qualcuno su Mangitalia.<br />
            <span className="font-sacramento text-4xl md:text-5xl">Guadagna insieme a lui.</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8">
            Ogni cliente che registri con il tuo codice ti fa guadagnare una percentuale sul suo primo ordine.
            Se lo convince anche a diventare produttore? Guadagni su tutto il suo anno di vendite.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="bg-white rounded-2xl border border-[#f3deb8] px-6 py-4 text-center min-w-[140px]">
              <div className="text-3xl font-black text-[#c78244] mb-1">🎁</div>
              <div className="text-sm text-gray-500">Primo ordine invitato</div>
            </div>
            <div className="bg-white rounded-2xl border border-[#f3deb8] px-6 py-4 text-center min-w-[140px]">
              <div className="text-3xl font-black text-[#c78244] mb-1">📈</div>
              <div className="text-sm text-gray-500">Sul venduto del produttore invitato</div>
            </div>
            <div className="bg-white rounded-2xl border border-[#f3deb8] px-6 py-4 text-center min-w-[140px]">
              <div className="text-3xl font-black text-[#c78244] mb-1">💳</div>
              <div className="text-sm text-gray-500">Prelievo reale se sei Closer</div>
            </div>
          </div>
          <Link
            to="/diventa-closer"
            className="inline-block bg-gradient-to-r from-[#F6CF48] to-[#c78244] text-white px-8 py-3.5 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-shadow"
          >
            Scopri il programma →
          </Link>
        </div>
      </motion.section>

      {/* Waitlist / newsletter */}
      <motion.section
        className="mang-gradient text-white text-center py-14 px-6 mt-4"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <h3 className="text-2xl md:text-3xl font-bold mb-3">Vuoi sapere quando puoi ordinare?</h3>
        <p className="text-lg max-w-xl mx-auto mb-6 text-white/90">
          Iscriviti: ti avvisiamo quando il catalogo alimentare passa dall&apos;anteprima agli ordini.
        </p>
        <motion.button
          type="button"
          onClick={() => {
            trackPartnerEvent('newsletter_open', { source: 'home_cta' });
            setShowModal(true);
          }}
          className="bg-white text-[#c78244] px-8 py-3 rounded-full font-bold text-lg shadow-xl"
          whileHover={{ scale: 1.05 }}
        >
          Avvisami al lancio
        </motion.button>
      </motion.section>

      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                onClick={() => setShowModal(false)}
                aria-label="Chiudi"
              >
                <FaTimes size={20} />
              </button>
              <h4 className="text-xl font-bold text-[#c78244] mb-4 text-center pr-6">
                Resta aggiornato
              </h4>
              <NewsletterSignup sorgente="home" interesse="lancio" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="py-14 flex flex-col items-center gap-3 px-4"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <p className="text-gray-400 text-base">Vuoi approfondire il progetto?</p>
        <motion.button
          type="button"
          onClick={() => navigate('/presentazione')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          className="relative px-12 py-4 rounded-full font-black text-lg text-white shadow-xl overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #F6CF48 0%, #c78244 60%, #9a5f2f 100%)' }}
        >
          Scopri la presentazione
        </motion.button>
      </motion.div>

      <footer className="border-t border-[#f3deb8] py-8 px-4 text-center text-sm text-gray-500 space-y-3">
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/termini" className="hover:text-[#c78244]">Termini</Link>
          <Link to="/privacy" className="hover:text-[#c78244]">Privacy</Link>
          <Link to="/per-partner" className="hover:text-[#c78244]">Per partner</Link>
          <Link to="/diventa-closer" className="hover:text-[#c78244]">Guadagna con noi</Link>
          <Link to="/chi-siamo" className="hover:text-[#c78244]">Chi siamo</Link>
          <Link to="/segnalazione" className="hover:text-[#c78244]">Segnalazione</Link>
        </div>
        <div className="mb-4 text-[#c78244] font-sacramento text-2xl md:text-3xl">
          Prodotti di qualità. Produttori di qualità. Consumatori di qualità.
        </div>
        <p>© {new Date().getFullYear()} Mangitalia — eccellenze alimentari italiane</p>
      </footer>
    </div>
  );
}
